/**
 * Authentication Store
 */

import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { supabase } from '@/lib/supabase'
import { config } from '@/config'
import type {
    AuthUser,
    UserProfile,
    Session,
    SignUpCredentials,
    SignInCredentials,
    OAuthProvider,
    EasyNavPrefs,
    EasyNavPersona,
} from '@/types/auth.types'

import { UserRole } from '@/types/auth.types'

export const useAuthStore = defineStore('auth', () => {
    // State
    const user = ref<AuthUser | null>(null)
    const profile = ref<UserProfile | null>(null)
    const session = ref<Session | null>(null)
    const isLoading = ref(true)
    const initialized = ref(false)
    const error = ref<string | null>(null)
    const isProfileLoading = ref(false)

    // Getters
    const isAuthenticated = computed(() => !!user.value && !!session.value)
    const token = computed(() => session.value?.access_token || null)

    const userRoles = computed((): UserRole[] => {
        return (profile.value?.roles as UserRole[]) || ['member']
    })

    const isAdmin = computed(() => userRoles.value.includes(UserRole.ADMINISTRATOR))
    const isCurator = computed(() => userRoles.value.includes(UserRole.CURATOR) || isAdmin.value)
    const isChapterLead = computed(() => userRoles.value.includes(UserRole.CHAPTER_LEAD) || isAdmin.value)
    // Only redirect to onboarding when we have a loaded profile that hasn't completed it.
    // If profile is null (fetch failed, race condition), do NOT redirect — that causes
    // false redirects to the onboarding screen after idle/token refresh.
    const needsOnboarding = computed(() => !!profile.value && !profile.value.onboarding_completed)

    // ── EasyNav computed state ──────────────────────────────────────────────
    const CACHE_KEY_EASYNAV = 'mondai-cache-easynav'
    let initialCachedEasyNav: any = null
    try {
        const rawCached = typeof localStorage !== 'undefined' ? localStorage.getItem(CACHE_KEY_EASYNAV) : null
        initialCachedEasyNav = rawCached ? JSON.parse(rawCached) : null
    } catch {
        initialCachedEasyNav = null
    }

    const easyNavPrefs = computed((): EasyNavPrefs => {
        const prefs = (profile.value?.agent_prefs as Record<string, any> | null | undefined)
        const raw = prefs?.easynav ?? initialCachedEasyNav
        return {
            enabled: raw?.enabled ?? false,
            persona: (raw?.persona as EasyNavPersona | null) ?? null,
            seen_welcome: raw?.seen_welcome ?? false,
            dismissed_pages: raw?.dismissed_pages ?? [],
        }
    })

    watch(() => profile.value?.agent_prefs?.easynav, (newVal) => {
        if (newVal && typeof localStorage !== 'undefined') {
            try {
                localStorage.setItem(CACHE_KEY_EASYNAV, JSON.stringify(newVal))
            } catch {
                // Ignore storage quota errors
            }
        }
    }, { immediate: true })

    const isEasyNavEnabled = computed(() => easyNavPrefs.value.enabled)
    const activePersona = ref<EasyNavPersona | null>(null)

    watch(() => easyNavPrefs.value.persona, (newVal) => {
        activePersona.value = newVal
    }, { immediate: true })

    const easyNavPersona = computed(() => activePersona.value)

    // Actions
    async function initialize() {
        isLoading.value = true
        error.value = null

        try {
            // Get current session
            const { data: { session: currentSession }, error: sessionError } = await supabase.auth.getSession()

            if (sessionError) throw sessionError

            if (currentSession) {
                session.value = currentSession as Session
                user.value = currentSession.user as AuthUser
                isProfileLoading.value = true
                // Non-blocking background fetch so initial boot is never blocked
                fetchProfile().catch(e => console.error('[AuthStore] Initial profile fetch failed:', e))
            }

            // Listen for auth changes
            supabase.auth.onAuthStateChange(async (event, newSession) => {
                // console.log(`[Auth] ${new Date().toISOString()} State change:`, event)

                // Always sync session/user state when a valid session is provided
                if (newSession) {
                    session.value = newSession as Session
                    user.value = newSession.user as AuthUser
                }

                switch (event) {
                    case 'SIGNED_IN':
                    case 'INITIAL_SESSION':
                        if (newSession) {
                            fetchProfile().catch(e => console.error('[AuthStore] AuthState profile hook failed:', e))
                        }
                        break
                    case 'SIGNED_OUT':
                        session.value = null
                        user.value = null
                        profile.value = null
                        break
                    case 'TOKEN_REFRESHED':
                        // console.log(`[Auth] ${new Date().toISOString()} Token refreshed successfully`)
                        break
                    case 'USER_UPDATED':
                        console.log(`[Auth] ${new Date().toISOString()} User updated`)
                        break
                }
            })
        } catch (e) {
            error.value = e instanceof Error ? e.message : 'Failed to initialize auth'
            console.error('Auth initialization error:', e)
        } finally {
            isLoading.value = false
            initialized.value = true
        }
    }


    async function fetchProfile() {
        if (!user.value) return
        isProfileLoading.value = true

        try {
            const { data, error: profileError } = await supabase
                .from('profiles')
                .select('*')
                .eq('auth_id', user.value.id)
                .single()

            if (profileError) {
                // Profile might not exist yet for new users
                if (profileError.code === 'PGRST116') {
                    // Try to claim a pending profile or create a new one
                    await claimPendingProfile()
                } else {
                    throw profileError
                }
            } else {
                profile.value = data as UserProfile
                // Non-blocking background check to ensure any unlinked chapter leads are resolved
                resolvePendingRoles(user.value.id, data.id, data.email, data.name)
            }
        } catch (e) {
            console.error('Failed to fetch profile:', e)
        } finally {
            isProfileLoading.value = false
        }
    }

    /**
     * Claim a pending profile by email match, or create a new profile.
     * Called when a user signs up and no profile is found by auth_id.
     */
    async function claimPendingProfile() {
        if (!user.value) return

        const userEmail = user.value.email?.toLowerCase().trim()
        if (!userEmail) return

        try {
            const userMetadata = user.value.user_metadata || {}
            const fullName = userMetadata.full_name || userMetadata.name || null
            const avatarUrl = userMetadata.avatar_url || userMetadata.picture || null

            // 1. Check for an existing profile with matching email that hasn't been claimed yet
            const { data: existingProfile } = await supabase
                .from('profiles')
                .select('*')
                .eq('email', userEmail)
                .is('auth_id', null)
                .maybeSingle()

            if (existingProfile) {
                // Claim the profile: set auth_id
                const { data: claimed, error: claimError } = await supabase
                    .from('profiles')
                    .update({
                        auth_id: user.value.id,
                        name: existingProfile.name || fullName,
                        avatar_url: existingProfile.avatar_url || avatarUrl,
                        updated_at: new Date().toISOString(),
                    })
                    .eq('id', existingProfile.id)
                    .select()
                    .single()

                if (claimError) {
                    console.error('[Auth] Failed to claim existing profile:', claimError)
                } else {
                    profile.value = claimed as UserProfile
                    console.log('[Auth] Claimed profile for:', userEmail)
                    // Pass both auth ID and profiles.id — chapter_lead_id must store profiles.id
                    resolvePendingRoles(user.value.id, existingProfile.id, userEmail, existingProfile.name || fullName)
                    return
                }
            }

            // 2. No pending profile found — create a new one
            const { data: newProfile, error: insertError } = await supabase
                .from('profiles')
                .insert({
                    auth_id: user.value.id,
                    email: userEmail,
                    name: fullName,
                    avatar_url: avatarUrl,
                    status: 'active',
                    onboarding_completed: false,
                    roles: ['member'],
                    joined: new Date().toISOString(),
                })
                .select()
                .single()

            if (insertError) {
                console.error('[Auth] Failed to create new profile:', insertError)
            } else {
                profile.value = newProfile as UserProfile
                // Pass both auth ID and profiles.id — chapter_lead_id must store profiles.id
                resolvePendingRoles(user.value.id, newProfile.id, userEmail, fullName)
            }
        } catch (e) {
            console.error('[Auth] Error in claimPendingProfile:', e)
        }
    }

    /**
     * Parse a potentially messy email or name field that may contain multiple tokens
     * separated by spaces, commas, semicolons, or any combination.
     * e.g. "alice@x.com; bob@x.com, carol@x.com  dave@x.com"
     */
    function parseLeadTokens(raw: string | null): string[] {
        if (!raw) return []
        return raw
            .split(/[\s,;]+/)
            .map(t => t.trim().toLowerCase())
            .filter(t => t.length > 0)
    }

    /**
     * Resolve pending chapter leads and co-organizers - called when a user signs up/in or completes onboarding.
     * Scans:
     * 1. chapters.chapter_lead_email tokens (matching user email or name) across all chapters (active & onboarding)
     * 2. chapters with chapter_lead_id / co_organizer_ids pointing to unlinked profiles (auth_id IS NULL)
     * 3. chapter_onboarding_pipeline prospect records matching prospect_email or prospect_name
     * 4. chapters where user profileId is already in co_organizer_ids
     *
     * Links chapter_lead_id, cleans lead email tokens, sets home_chapter_id, and grants chapter_lead role.
     *
     * @param authId   - auth.users.id  (used to find/update the profile by auth_id)
     * @param profileId - profiles.id   (stored as chapter_lead_id / in co_organizer_ids)
     * @param userEmail - the registering user's email
     * @param userName  - optional user's name
     */
    async function resolvePendingRoles(authId: string, profileId: string, userEmail: string, userName?: string | null) {
        if (!userEmail && !profileId) return

        const emailLower = (userEmail || '').toLowerCase().trim()
        const nameLower = (userName || '').toLowerCase().trim()
        let shouldGrantLeadRole = false
        let matchedChapterId: string | null = null

        try {
            // Run parallel lookups across candidate sources
            const [leadEmailChaptersRes, unlinkedProfilesRes, pipelineChaptersRes, coOrgChaptersRes] = await Promise.all([
                // 1. Chapters where chapter_lead_email contains userEmail or userName
                emailLower
                    ? supabase
                        .from('chapters')
                        .select('id, name, chapter_lead_id, chapter_lead_email, co_organizer_ids, status')
                        .ilike('chapter_lead_email', `%${emailLower}%`)
                    : Promise.resolve({ data: [] as any[], error: null }),

                // 2. Unlinked profiles that have the same email or name (where auth_id IS NULL)
                emailLower
                    ? supabase
                        .from('profiles')
                        .select('id, email, name, auth_id')
                        .is('auth_id', null)
                        .eq('email', emailLower)
                    : Promise.resolve({ data: [] as any[], error: null }),

                // 3. Chapter onboarding pipeline records matching prospect_email or prospect_name
                emailLower
                    ? supabase
                        .from('chapter_onboarding_pipeline')
                        .select('id, chapter_id, prospect_email, prospect_name, profile_state')
                        .ilike('prospect_email', emailLower)
                    : Promise.resolve({ data: [] as any[], error: null }),

                // 4. Chapters where profileId is already in co_organizer_ids or chapter_lead_id
                supabase
                    .from('chapters')
                    .select('id, name, chapter_lead_id, co_organizer_ids')
                    .or(`chapter_lead_id.eq.${profileId},co_organizer_ids.cs.{${profileId}}`)
            ])

            if (coOrgChaptersRes.data && coOrgChaptersRes.data.length > 0) {
                console.log(`[Auth] resolvePendingRoles found chapter leadership for ${userEmail}:`, coOrgChaptersRes.data)
            }

            // ── A. Handle Chapter Lead Email Matches ──────────────────────────────────
            const leadEmailChapters = leadEmailChaptersRes.data || []
            for (const c of leadEmailChapters) {
                const tokens = parseLeadTokens(c.chapter_lead_email)
                const isMatched = (emailLower && tokens.some(t => t.includes(emailLower) || emailLower.includes(t))) ||
                                  (nameLower && nameLower.length >= 3 && tokens.some(t => t === nameLower || t.includes(nameLower)))

                if (isMatched) {
                    const isFirstToken = tokens.length > 0 && (
                        (emailLower && (tokens[0].includes(emailLower) || emailLower.includes(tokens[0]))) ||
                        (nameLower && (tokens[0] === nameLower || tokens[0].includes(nameLower)))
                    )
                    const hasNoLead = !c.chapter_lead_id || c.chapter_lead_id === profileId

                    const remainingTokens = tokens.filter(t => !t.includes(emailLower) && !(nameLower && t.includes(nameLower)))
                    const nextLeadEmail = remainingTokens.length > 0 ? remainingTokens.join(', ') : null

                    if (isFirstToken && hasNoLead) {
                        const { error: updateErr } = await supabase
                            .from('chapters')
                            .update({
                                chapter_lead_id: profileId,
                                chapter_lead_email: nextLeadEmail
                            })
                            .eq('id', c.id)

                        if (!updateErr) {
                            shouldGrantLeadRole = true
                            matchedChapterId = c.id
                            console.log(`[Auth] Resolved pending lead for ${c.name}: ${userEmail}`)
                        }
                    } else {
                        const currentCoOrgIds = c.co_organizer_ids || []
                        const newCoOrgIds = currentCoOrgIds.includes(profileId)
                            ? currentCoOrgIds
                            : [...currentCoOrgIds, profileId]

                        const { error: updateErr } = await supabase
                            .from('chapters')
                            .update({
                                co_organizer_ids: newCoOrgIds,
                                chapter_lead_email: nextLeadEmail
                            })
                            .eq('id', c.id)

                        if (!updateErr) {
                            shouldGrantLeadRole = true
                            matchedChapterId = c.id
                            console.log(`[Auth] Resolved pending co-organizer for ${c.name}: ${userEmail}`)
                        }
                    }
                }
            }

            // ── B. Handle Unlinked Profile IDs Linked to Chapters ─────────────────────
            const unlinkedProfiles = unlinkedProfilesRes.data || []
            if (unlinkedProfiles.length > 0) {
                const unlinkedIds = unlinkedProfiles.map(p => p.id).filter(id => id !== profileId)
                if (unlinkedIds.length > 0) {
                    const { data: chaptersWithUnlinked } = await supabase
                        .from('chapters')
                        .select('id, name, chapter_lead_id, co_organizer_ids')
                        .in('chapter_lead_id', unlinkedIds)

                    for (const ch of (chaptersWithUnlinked || [])) {
                        const { error: updateErr } = await supabase
                            .from('chapters')
                            .update({ chapter_lead_id: profileId })
                            .eq('id', ch.id)

                        if (!updateErr) {
                            shouldGrantLeadRole = true
                            matchedChapterId = ch.id
                            console.log(`[Auth] Re-linked unlinked chapter lead on ${ch.name} to registered user: ${userEmail}`)
                        }
                    }
                }
            }

            // ── C. Handle Pipeline Records for Onboarding Chapters ─────────────────────
            const pipelineRecords = (pipelineChaptersRes.data || []).filter((p: any) => p.profile_state !== 'registered')
            for (const p of pipelineRecords) {
                if (p.chapter_id) {
                    const { data: pipelineChapter } = await supabase
                        .from('chapters')
                        .select('id, name, chapter_lead_id, status')
                        .eq('id', p.chapter_id)
                        .maybeSingle()

                    if (pipelineChapter && (!pipelineChapter.chapter_lead_id || pipelineChapter.chapter_lead_id === profileId)) {
                        const { error: updateErr } = await supabase
                            .from('chapters')
                            .update({ chapter_lead_id: profileId })
                            .eq('id', pipelineChapter.id)

                        if (!updateErr) {
                            shouldGrantLeadRole = true
                            matchedChapterId = pipelineChapter.id
                            console.log(`[Auth] Linked onboarding chapter lead for ${pipelineChapter.name}: ${userEmail}`)
                            // Mark pipeline record as registered once claimed so it doesn't re-trigger indefinitely
                            await supabase
                                .from('chapter_onboarding_pipeline')
                                .update({ profile_state: 'registered' })
                                .eq('id', p.id)
                        }
                    }
                }
            }

            // ── D. Check existing chapter lead or co-organizers ─────────────────────────
            const coOrgChapters = coOrgChaptersRes.data || []
            if (coOrgChapters.length > 0) {
                shouldGrantLeadRole = true
                if (!matchedChapterId) matchedChapterId = coOrgChapters[0].id
            }

            // ── E. Grant the chapter_lead role & set home chapter if matched ────────────
            if (shouldGrantLeadRole) {
                const { data: currentProfile } = await supabase
                    .from('profiles')
                    .select('roles, home_chapter_id')
                    .eq('auth_id', authId)
                    .single()

                if (currentProfile) {
                    const currentRoles: UserRole[] = currentProfile.roles || [UserRole.MEMBER]
                    const updates: Record<string, any> = {}

                    if (!currentRoles.includes(UserRole.CHAPTER_LEAD)) {
                        updates.roles = [...currentRoles, UserRole.CHAPTER_LEAD]
                    }
                    if (!currentProfile.home_chapter_id && matchedChapterId) {
                        updates.home_chapter_id = matchedChapterId
                    }

                    if (Object.keys(updates).length > 0) {
                        const { error: roleError } = await supabase
                            .from('profiles')
                            .update(updates)
                            .eq('auth_id', authId)

                        if (!roleError && profile.value) {
                            if (updates.roles) profile.value.roles = updates.roles
                            if (updates.home_chapter_id) profile.value.home_chapter_id = updates.home_chapter_id
                        }
                    }
                }
            }
        } catch (e) {
            console.error('Error in resolvePendingRoles:', e)
        }
    }

    async function signUp(credentials: SignUpCredentials) {
        isLoading.value = true
        error.value = null

        try {
            // Strictly use SITE_URL if available, otherwise Supabase uses default project URL
            const siteUrl = config.auth.siteUrl.replace(/\/$/, '')
            const options: any = {
                data: {
                    full_name: credentials.name,
                },
            }

            if (siteUrl) {
                options.emailRedirectTo = `${siteUrl}/auth/callback`
            }

            const { data, error: signUpError } = await supabase.auth.signUp({
                email: credentials.email,
                password: credentials.password,
                options,
            })

            if (signUpError) throw signUpError

            // Supabase email enumeration protection: if the email is already registered,
            // it returns success but the identities array is empty.
            if (data?.user?.identities && data.user.identities.length === 0) {
                throw new Error('This email is already registered. Please sign in instead.')
            }

            return { success: true, data }
        } catch (e) {
            error.value = e instanceof Error ? e.message : 'Sign up failed'
            return { success: false, error: error.value }
        } finally {
            isLoading.value = false
        }
    }

    async function signIn(credentials: SignInCredentials) {
        isLoading.value = true
        error.value = null

        try {
            const { data, error: signInError } = await supabase.auth.signInWithPassword({
                email: credentials.email,
                password: credentials.password,
            })

            if (signInError) throw signInError

            // Ensure profile is loaded so easyNavPrefs and user roles are ready immediately
            await fetchProfile()

            return { success: true, data }
        } catch (e) {
            error.value = e instanceof Error ? e.message : 'Sign in failed'
            return { success: false, error: error.value }
        } finally {
            isLoading.value = false
        }
    }

    async function signInWithOAuth(provider: OAuthProvider) {
        isLoading.value = true
        error.value = null

        try {
            const origin = (typeof window !== 'undefined' && window.location?.origin)
                ? window.location.origin
                : (config.auth.siteUrl || '').replace(/\/$/, '')

            const options: any = {}

            if (origin) {
                options.redirectTo = `${origin.replace(/\/$/, '')}/auth/callback`
            }

            const { data, error: oauthError } = await supabase.auth.signInWithOAuth({
                provider,
                options,
            })

            if (oauthError) throw oauthError

            return { success: true, data }
        } catch (e) {
            error.value = e instanceof Error ? e.message : 'OAuth sign in failed'
            return { success: false, error: error.value }
        } finally {
            isLoading.value = false
        }
    }

    async function signOut() {
        isLoading.value = true
        error.value = null

        try {
            const { error: signOutError } = await supabase.auth.signOut()
            if (signOutError) throw signOutError

            session.value = null
            user.value = null
            profile.value = null
            localStorage.removeItem('mondai-cache-matchmaking')

            return { success: true }
        } catch (e) {
            error.value = e instanceof Error ? e.message : 'Sign out failed'
            // Force clear state even if API fails
            session.value = null
            user.value = null
            profile.value = null
            localStorage.removeItem('mondai-cache-matchmaking')
            return { success: false, error: error.value }
        } finally {
            isLoading.value = false
        }
    }

    async function updateProfile(updates: Partial<UserProfile>) {
        if (!user.value) return { success: false, error: 'Not authenticated' }

        try {
            const { data, error: updateError } = await supabase
                .from('profiles')
                .update({
                    ...updates,
                    updated_at: new Date().toISOString()
                })
                .eq('auth_id', user.value.id)
                .select()
                .single()

            if (updateError) throw updateError

            profile.value = data as UserProfile
            return { success: true, data }
        } catch (e) {
            const errorMessage = e instanceof Error ? e.message : 'Failed to update profile'
            return { success: false, error: errorMessage }
        }
    }

    async function completeOnboarding(profileData: Partial<UserProfile>) {
        const result = await updateProfile({
            ...profileData,
            status: 'active',
            show_socials: profileData.show_socials ?? true,
            onboarding_completed: true,
        })
        if (result.success && profile.value && user.value) {
            await resolvePendingRoles(
                user.value.id,
                profile.value.id,
                profile.value.email || user.value.email || '',
                profile.value.name || profileData.name || ''
            )
        }
        return result
    }

    /**
     * Deep-merge new prefs into agent_prefs and persist to Supabase.
     * Existing keys not present in `prefs` are preserved.
     */
    async function updateAgentPrefs(prefs: Record<string, any>) {
        if (!user.value || !profile.value) return { success: false, error: 'Not authenticated' }
        const current = (profile.value.agent_prefs as Record<string, any> | null) ?? {}
        const merged = { ...current, ...prefs }
        return updateProfile({ agent_prefs: merged })
    }

    /**
     * Update the EasyNav sub-section of agent_prefs only.
     */
    async function updateEasyNavPrefs(patch: Partial<EasyNavPrefs>) {
        const current = easyNavPrefs.value
        if (patch.persona !== undefined) {
            activePersona.value = patch.persona
        }
        return updateAgentPrefs({ easynav: { ...current, ...patch } })
    }

    function setActivePersona(p: EasyNavPersona | null) {
        activePersona.value = p
    }

    async function resetPassword(email: string) {
        try {
            const siteUrl = config.auth.siteUrl.replace(/\/$/, '')
            let redirectTo = siteUrl ? `${siteUrl}/auth/reset-password` : undefined

            // Check for custom override from global settings
            try {
                const { data: setting } = await supabase
                    .from('global_settings')
                    .select('value')
                    .eq('key', 'pw_reset_url')
                    .eq('server_name', config.serverName)
                    .maybeSingle()

                if (setting?.value && (setting.value.startsWith('http://') || setting.value.startsWith('https://'))) {
                    redirectTo = setting.value
                }
            } catch (settingsError) {
                console.warn('[Auth] Failed to fetch custom reset URL, falling back to default:', settingsError)
            }

            const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
                redirectTo,
            })

            if (resetError) throw resetError

            return { success: true }
        } catch (e) {
            return { success: false, error: e instanceof Error ? e.message : 'Failed to send reset email' }
        }
    }

    async function updatePassword(password: string) {
        isLoading.value = true
        error.value = null

        try {
            const { error: updateError } = await supabase.auth.updateUser({ password })

            if (updateError) throw updateError

            return { success: true }
        } catch (e) {
            error.value = e instanceof Error ? e.message : 'Failed to update password'
            return { success: false, error: error.value }
        } finally {
            isLoading.value = false
        }
    }

    async function deleteAccount() {
        if (!user.value) return { success: false, error: 'Not authenticated' }

        try {
            const { error: deleteError } = await supabase
                .from('profiles')
                .delete()
                .eq('auth_id', user.value.id)

            if (deleteError) throw deleteError

            // Profile deleted successfully, sign the user out
            await signOut()
            return { success: true }
        } catch (e) {
            return { success: false, error: e instanceof Error ? e.message : 'Failed to delete account' }
        }
    }

    return {
        // State
        user,
        profile,
        session,
        token,
        isLoading,
        initialized,
        error,
        isProfileLoading,

        // Getters
        isAuthenticated,
        userRoles,
        isAdmin,
        isCurator,
        isChapterLead,
        needsOnboarding,
        easyNavPrefs,
        isEasyNavEnabled,
        easyNavPersona,
        activePersona,

        // Actions
        initialize,
        signUp,
        signIn,
        signInWithOAuth,
        signOut,
        updateProfile,
        updateAgentPrefs,
        updateEasyNavPrefs,
        setActivePersona,
        completeOnboarding,
        resetPassword,
        updatePassword,
        fetchProfile,
        deleteAccount,
    }
})
